import React, {useContext, useMemo, useState} from 'react';
import {useFieldArray, useFormContext} from 'react-hook-form';
import {FormGroup, Table} from 'reactstrap';
import {FormAbstractFieldProps, FormFieldConfigSize, FormFieldOption} from '@src/@types/components/Form';
import FormField from '@src/components/Form/FormField';
import {UserRolesFormModel, UserRolesFormPreparedPermission} from '@src/@types/components/UserRolesForm';
import Translate from '@src/components/Translate/Translate';
import {studlyCase} from '@src/helpers/string-registres';
import classnames from '@src/helpers/classnames';
import CustomSelect from '@src/components/Fields/CustomSelect';
import app from '@src/helpers/app';
import trans from '@src/helpers/trans';
import useFormValue from '@src/hooks/useFormValue';
import usePermissionGranted from '@src/hooks/usePermissionGranted';
import {LocaleContext} from '@src/contexts/LocaleContext';

type Field = (UserRolesFormPreparedPermission & { id: string });

const emptyObject: Record<any, never> = {};

export default function UserRolePermissionsList<Model extends UserRolesFormModel = UserRolesFormModel>(
        props: FormAbstractFieldProps<Model, string>
) {
    const {control, register} = useFormContext();
    const {locale} = useContext(LocaleContext);
    const {currentValue: currentParent} = useFormValue('parent');
    const fieldArrayApi = useFieldArray({control, name: props.fieldName as string});
    const [expandedModels, setExpandedModels] = useState<Record<string, boolean>>({});
    const [selectedModels, setSelectedModels] = useState<Record<string, boolean>>({});
    const [searchStrings, setSearchStrings] = useState<Record<string, string>>({});

    const canSeeParentField = usePermissionGranted('super-admin-only');
    const fields = fieldArrayApi.fields as Field[];
    const [fieldsMap, onModelsFilterValueChanged] = useMemo(() => {
        const fieldsMap = fields.reduce<Record<string, number>>((res, field, index) => {
            res[field.name] = index;

            return res;
        }, {});

        const onModelsFilterValueChanged: React.ComponentProps<typeof CustomSelect<true>>['onChange'] = (payload) => {
            const map = payload.reduce<typeof selectedModels>((res, model) => {
                if (model) {
                    res[model] = true;
                }

                return res;
            }, {});

            setSelectedModels(map);
            setExpandedModels(old => ({...old, ...map}));
        };

        return [fieldsMap, onModelsFilterValueChanged];
    }, [fields]);

    const allPermissions = props.model?.all_existing_permissions_map ?? emptyObject;
    const hasParent = canSeeParentField && currentParent != null || props.model?.parent_id != null;

    const [selectedModelsKeys, allPermissionsKeys, isVisibleField, setValuesForModel] = useMemo(() => {
        const selectedModelsKeys = Object.keys(selectedModels);
        const allPermissionsKeys = Object.keys(allPermissions);

        const availableLanguages: string[] = app('config').get('client.available-languages', []);
        const isVisibleField = (modelName: string, fieldLabel?: string): boolean => {
            if (!searchStrings[modelName]) {
                return true;
            }

            const searchTerm = searchStrings[modelName].toLowerCase();

            return fieldLabel != null && (
                    fieldLabel.toLowerCase().includes(searchTerm) ||
                    availableLanguages.some(
                            lang => trans(fieldLabel, lang).toLowerCase().includes(searchTerm)
                    )
            );
        };

        const setValuesForModel = (modelName: string, value: string): void => {
            Object.keys(allPermissions[modelName]).forEach(name => {
                if (isVisibleField(modelName, allPermissions[modelName][name]?.label)) {
                    fieldArrayApi.update(fieldsMap[name], {...fields[fieldsMap[name]], value});
                }
            });
        };

        return [selectedModelsKeys, allPermissionsKeys, isVisibleField, setValuesForModel];
    }, [allPermissions, fieldArrayApi, fields, fieldsMap, searchStrings, selectedModels]);

    const getModelField = <T extends boolean>(modelName: string, field: string, withDefault: T): T extends true ? string : string | null => {
        const firstRuleName = (Object.values(allPermissions?.[modelName] ?? {})[0] ?? null).label;
        if (firstRuleName) {
            const result = trans(
                    firstRuleName.replace(/(.+)\.([^.]+)/uim, `$1.${field}`),
                    locale,
                    null
            );
            if (result) {
                return result;
            }
        }

        return trans(
                `Permissions.${modelName}.${field}`,
                locale,
                withDefault ? studlyCase(modelName) : null
        );
    };
    const getModelTitle = (modelName: string): string => {
        return getModelField(modelName, 'title', true);
    };
    const getModelDescription = (modelName: string): string | null => {
        return getModelField(modelName, 'description', false);
    };

    if (!props.model || !fields.length) {
        return null;
    }

    return <FormField {...props} type="text" withoutLayout>
        <div className="mb-3 pb-2 border-bottom">
            <CustomSelect<true>
                    label="Model"
                    placeholder="UserRolePermissionsList.models-filter-placeholder"
                    value={selectedModelsKeys}
                    options={allPermissionsKeys.reduce((res, key) => {
                        res.push({
                            value: key,
                            label: getModelTitle(key),
                        });

                        return res;
                    }, [] as FormFieldOption[])}
                    onChange={onModelsFilterValueChanged}
                    size={FormFieldConfigSize.SM}
                    closeMenuOnSelect
                    clearable
                    multiple
            />
        </div>

        {allPermissionsKeys.map(modelName => {
            if (
                    selectedModelsKeys.length && (
                            !(modelName in selectedModels) ||
                            !selectedModels[modelName]
                    )
            ) {
                return null;
            }

            // noinspection PointlessBooleanExpressionJS
            const isExpanded = expandedModels[modelName] === true;
            const isCollapsed = !isExpanded;

            const description = getModelDescription(modelName);

            return <Table key={modelName + '-permissions'} className="text-center" size="sm" hover>
                <thead>
                    <tr
                            className="bg-light cursor-pointer"
                            tabIndex={0}
                            onClick={() => {
                                setExpandedModels({
                                    ...expandedModels,
                                    [modelName]: !isExpanded,
                                });
                            }}
                    >
                        <th className="text-start" style={{verticalAlign: 'middle'}}>
                            <div className="d-flex align-items-center">
                                <Translate
                                        message="UserRolePermissionsList.model-name"
                                        replace={{name: getModelTitle(modelName)}}
                                        className="me-2 flex-shrink-0"
                                />

                                <input
                                        type="search"
                                        className="form-control form-control-sm"
                                        value={searchStrings[modelName] ?? ''}
                                        placeholder="Type to search"
                                        onClick={e => e.stopPropagation()}
                                        onChange={(event) => {
                                            setSearchStrings(map => {
                                                return {...map, [modelName]: event.target.value};
                                            });
                                        }}
                                />
                            </div>
                        </th>
                        <td className="text-end" colSpan={hasParent ? 3 : 2}>
                            <button type="button" className="btn btn-outline-info btn-sm">
                                <i className={`fas fa-chevron-${isCollapsed ? 'down' : 'up'}`}/>
                            </button>
                        </td>
                    </tr>
                    {isExpanded ? <tr>
                        <Translate
                                message="UserRolePermissionsList.headers.title"
                                className="text-start"
                                tag="th"
                        />
                        <Translate
                                message="UserRolePermissionsList.headers.allow"
                                tag="th"
                                onClick={() => setValuesForModel(modelName, '1')}
                        />
                        {hasParent ? (
                                <Translate
                                        message="UserRolePermissionsList.headers.inherit"
                                        tag="th"
                                        onClick={() => setValuesForModel(modelName, '-')}
                                />) : null}
                        <Translate
                                message="UserRolePermissionsList.headers.disallow"
                                tag="th"
                                onClick={() => setValuesForModel(modelName, '0')}
                        />
                    </tr> : null}
                </thead>
                {isExpanded ? <tbody>
                    {description ? <tr>
                        <td colSpan={3 + (hasParent ? 1 : 0)}>
                            <div className="alert alert-info text-start p-1 px-2 m-0">
                                <i className="fa fa-info-circle me-2"/>
                                {description}
                            </div>
                        </td>
                    </tr> : null}
                    {Object.keys(allPermissions[modelName]).map(name => {
                        const fieldIndex = fieldsMap[name];
                        const fieldData = fields[fieldIndex];
                        const permissionData = allPermissions[modelName][name];

                        const field = {
                            name,
                            id: fieldData?.id ?? `${name}--row`,
                            label: permissionData?.label,
                            description: permissionData?.description,
                        };

                        if (!isVisibleField(modelName, field.label)) {
                            return null;
                        }

                        const registerOption = () => register(`${String(props.fieldName)}.${fieldIndex}.value`);
                        const renderInput = (value: string) => {
                            return <FormGroup tag="td" className={classnames({
                                'cursor-pointer': true,
                                'form-radio-success': value === '1',
                                'form-radio-danger': value === '0',
                            })} onClick={e => {
                                if (e.currentTarget.tagName === 'TD') {
                                    fieldArrayApi.update(fieldIndex, {...fieldData, value});
                                }
                            }}>
                                <input
                                        type="radio"
                                        className="form-check-input"
                                        {...registerOption()}
                                        value={value}
                                />
                            </FormGroup>;
                        };

                        return <tr key={field.id}>
                            <td width="70%" className="text-start ps-3">
                                <Translate tag="div" message={field.label ?? field.name}/>
                                {field.description ? <Translate
                                        tag="div"
                                        message={field.description}
                                        className="form-text text-muted"
                                /> : null}
                            </td>
                            {renderInput('1')}
                            {hasParent ? renderInput('-') : null}
                            {renderInput('0')}
                        </tr>;
                    })}
                </tbody> : null}
            </Table>;
        })}
    </FormField>;
}
